import React, { useEffect } from 'react';
import { head } from 'ramda';
import { isBlank } from 'utils/helperMethods';
import { ConnectedRouter } from 'connected-react-router';
import { Provider } from 'react-redux';
import Favicon from 'react-favicon';
import { Provider as RollbarProvider } from '@rollbar/react';

import useApp from 'reducers/hooks/useApp';
import bundleIcon from 'public/images/favicon.png';
import { initialize } from 'utils/gtmHelper';
import useScreen, { initializeWindowSize } from 'hooks/useScreen';
import { useLocation } from 'react-router-dom';
import Loader from 'sharedComponents/Loader';
import configureRollbar from 'utils/rollbar';

import useFirebaseChat from 'reducers/hooks/firebase/useChat';
import useChats from 'reducers/hooks/useChats';
import useBidRequests from 'reducers/hooks/useBidRequests';
import useLoanOfficerDashboard from 'reducers/hooks/useLoanOfficerDashboard';

import UserPresenter from 'presenters/UserPresenter';
import FirebaseChatPresenter from 'presenters/FirebaseChatPresenter';

import AppRoutes from './AppRoutes';
import PopupManager from './PopupManager';
import ThemeProvider from './layouts/ThemeProvider';

const tagManagerArgs = {
  gtmId: 'GTM-TG5F4X9',
  dataLayer: {
    originalLocation: `${document.location.protocol}//${document.location.hostname}${document.location.pathname}${document.location.search}`,
  },
};

initializeWindowSize();
initialize(tagManagerArgs);

const App = () => {
  const { isDesktop } = useScreen();
  const { loadCurrentUser, isCurrentUserIdle, isCurrentUserLoading, currentUser } = useApp();
  const isLoading = isCurrentUserIdle || isCurrentUserLoading;
  const isLoanOfficer = UserPresenter.isLoanOfficer(currentUser);
  const isBuyer = UserPresenter.isBuyer(currentUser);

  const location = useLocation();
  const { pathname } = location;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  const {
    authFirebase,
    setInitialized,
    initialized,
    setUserOnlineStatus,
    setOfflineStatusOnDisconnect,
    firebaseAuthStatus,
    isAuthenticatedOnFirebase,
    loadAndSubscribeToChats,
    subscribeToUsersOnlineStatuses,
    needToLoadChatsInfo,
    loadedChatsInfo,
    changeSelectedChatUid,
    loadAndSubscribeToMessages,
    chats,
    needToSelectChat,
    setNeedToSelectChat,
  } = useFirebaseChat();

  const { loadBidRequests } = useBidRequests();
  const { loadChats } = useChats();
  const { loadLoanOfficerDashboard } = useLoanOfficerDashboard();

  useEffect(() => {
    loadCurrentUser();
  }, []);

  const initilizeChats = () => {
    authFirebase();
  };

  useEffect(() => {
    if (needToLoadChatsInfo) {
      loadedChatsInfo();
      setTimeout(() => {
        loadChats();
        if (isLoanOfficer) {
          loadBidRequests();
        }
      }, 20000);
    }
  }, [needToLoadChatsInfo]);

  useEffect(() => {
    if (isAuthenticatedOnFirebase) {
      setUserOnlineStatus(currentUser.id);
      setOfflineStatusOnDisconnect(currentUser.id);
      loadAndSubscribeToChats(currentUser.id);
      subscribeToUsersOnlineStatuses();
    }
  }, [firebaseAuthStatus]);

  useEffect(() => {
    if (isBlank(currentUser) || initialized) return;
    if (!isBuyer && !isLoanOfficer) return;

    initilizeChats();
    loadChats();
    if (isLoanOfficer) {
      loadLoanOfficerDashboard();
      loadBidRequests();
    }
    setInitialized(true);
  }, [currentUser]);

  useEffect(() => {
    if (needToSelectChat) {
      setNeedToSelectChat(false);
      if (isBlank(chats)) return;

      if (isDesktop) {
        const firstChat = head(chats);
        const uid = FirebaseChatPresenter.uid(firstChat);
        changeSelectedChatUid(uid);
        loadAndSubscribeToMessages(uid);
      }
    }
  }, [needToSelectChat]);

  return (
    <ThemeProvider>
      <Favicon url={[bundleIcon]} />
      <PopupManager />
      {isLoading ? <Loader fullFill /> : <AppRoutes />}
    </ThemeProvider>
  );
};

const Root = ({ store, history }) => {
  const rollbarConfig = configureRollbar();

  return (
    <Provider store={store}>
      <RollbarProvider config={rollbarConfig}>
        <ConnectedRouter history={history}>
          <App />
        </ConnectedRouter>
      </RollbarProvider>
    </Provider>
  );
};

export default Root;



// WEBPACK FOOTER //
// ./src/App.js